import React, { useState, useEffect } from "react";
import { FiEdit, FiTrash2, FiChevronDown, FiChevronUp } from "react-icons/fi";
import SummaryApi from "../common";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const CurriculumStep = ({ courseId, courseTitle, isDarkMode }) => {
  const [showTopicModal, setShowTopicModal] = useState(false);
  const [newTopicData, setNewTopicData] = useState({
    topic_name: "",
    day_type: "",
    is_active: true,
  });

  const [showLessonModal, setShowLessonModal] = useState(null);
  const [lessonData, setLessonData] = useState({
    name: "",
    video: "",
    description: "",
    duration: "",
    media_path: "",
    lesson_thumbnail: null,
  });

  const [topics, setTopics] = useState([]);
  const [isEditingTopic, setIsEditingTopic] = useState(false);
  const [editingTopicId, setEditingTopicId] = useState(null);

  const [isEditingLesson, setIsEditingLesson] = useState(false);
  const [editingLessonId, setEditingLessonId] = useState(null);

  const [isSubmittingLesson, setIsSubmittingLesson] = useState(false);
  const [loadingLessonsFor, setLoadingLessonsFor] = useState(null);

  // ---------- THUMBNAIL & IMAGE URL HELPERS ----------
  const formatThumbnailUrl = (path) => {
    if (!path) return "";
    if (path.startsWith("http")) return path;
    const base = SummaryApi.baseUrl?.replace(/\/api\/?$/, "") || "";
    const cleanPath = path.replace(/\\/g, "/");
    return `${base}/${cleanPath}`;
  };

  const formatImageUrl = (imagePath) => {
    if (!imagePath) return "";
    if (imagePath.startsWith("http")) return imagePath;
    const baseUrl = SummaryApi.Images.url.replace("/api", "");
    if (imagePath.startsWith("uploads/")) return `${baseUrl}/${imagePath}`;
    return `${baseUrl}/${imagePath}`;
  };
  // -----------------------------------------

  // Fetch curriculum
  useEffect(() => {
    const fetchCurriculum = async () => {
      if (!courseId) {
        setTopics([]);
        return;
      }

      try {
        const response = await fetch(SummaryApi.getCurriculumByCourseId.url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ course_id: courseId }),
        });

        const data = await response.json();

        if (data.status) {
          const fetchedTopics = data.data.map((item) => ({
            id: item.curriculum_id,
            topic_name: item.topic_name,
            duration: item.duration || 0,
            day_type: item.day_type,
            course_id: item.course_id,
            is_active: item.is_active,
            isOpen: false,
            lessons: [],
            totalDuration: 0,
          }));
          setTopics(fetchedTopics);
        } else {
          setTopics([]);
        }
      } catch (error) {
        console.error("Error fetching curriculum:", error);
        setTopics([]);
      }
    };

    fetchCurriculum();
  }, [courseId]);

  // Fetch lessons
  const fetchLessonsForTopic = async (curriculumId) => {
    try {
      const response = await fetch(SummaryApi.getCourseLessonByCurriculumId.url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ curriculum_id: curriculumId }),
      });
      const data = await response.json();
      if (data.status) {
        return data.data;
      } else {
        return [];
      }
    } catch (error) {
      console.error("Error fetching lessons:", error);
      return [];
    }
  };

  // Toggle topic & load lessons
  const toggleTopic = async (id) => {
    setTopics((prev) =>
      prev.map((topic) =>
        topic.id === id ? { ...topic, isOpen: !topic.isOpen } : topic
      )
    );

    const topic = topics.find((t) => t.id === id);
    if (topic && !topic.isOpen && !topic.lessons.length) {
      setLoadingLessonsFor(id);
      const lessons = await fetchLessonsForTopic(id);
      const totalDuration = lessons.reduce((sum, l) => sum + (parseInt(l.duration) || 0), 0);

      setTopics((prev) =>
        prev.map((t) =>
          t.id === id ? { ...t, lessons, totalDuration } : t
        )
      );
      setLoadingLessonsFor(null);
    }
  };

  // Calculate total duration for a topic
  const calculateTotalDuration = (lessons) => {
    return lessons.reduce((sum, lesson) => sum + (parseInt(lesson.duration) || 0), 0);
  };

  // Save Topic
  const handleSaveTopic = async () => {
    if (!courseId) {
      toast.error("Course ID is missing.");
      return;
    }
    if (newTopicData.topic_name.trim() === "") {
      toast.error("Topic name cannot be empty.");
      return;
    }

    const payload = {
      course_id: courseId,
      topic_name: newTopicData.topic_name,
      day_type: newTopicData.day_type,
      is_active: newTopicData.is_active ? 1 : 0,
      ...(isEditingTopic && { curriculum_id: editingTopicId }),
    };

    try {
      const apiUrl = isEditingTopic
        ? SummaryApi.updateCurriculum.url
        : SummaryApi.createCurriculum.url;

      const res = await fetch(apiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (res.ok && data.status) {
        toast.success(isEditingTopic ? "Topic updated!" : "Topic created!");

        if (isEditingTopic) {
          setTopics((prev) =>
            prev.map((t) =>
              t.id === editingTopicId
                ? {
                    ...t,
                    topic_name: newTopicData.topic_name,
                    day_type: newTopicData.day_type,
                    is_active: newTopicData.is_active,
                  }
                : t
            )
          );
        } else {
          const newTopicObject = {
            id: data.curriculum_id,
            topic_name: newTopicData.topic_name,
            day_type: newTopicData.day_type,
            course_id: courseId,
            is_active: newTopicData.is_active,
            isOpen: true,
            lessons: [],
            totalDuration: 0,
            duration: 0,
          };
          setTopics((prevTopics) => [...prevTopics, newTopicObject]);
        }

        setNewTopicData({
          topic_name: "",
          day_type: "",
          is_active: true,
        });
        setIsEditingTopic(false);
        setEditingTopicId(null);
        setShowTopicModal(false);
      } else {
        toast.error("Failed to save topic: " + (data.message || "Unknown error"));
      }
    } catch (error) {
      console.error("Error saving topic:", error);
      toast.error("An error occurred while saving the topic.");
    }
  };

  // Save Lesson & Update Topic Duration
  const handleSaveLesson = async () => {
    if (!courseId || !showLessonModal) {
      toast.error("Missing course ID or curriculum ID.");
      return;
    }

    if (!lessonData.name || !lessonData.description || !lessonData.duration) {
      toast.error("Please fill all required fields.");
      return;
    }

    setIsSubmittingLesson(true);

    const formData = new FormData();
    formData.append("course_id", courseId);
    formData.append("curriculum_id", showLessonModal);
    formData.append("lesson_name", lessonData.name);
    formData.append("description", lessonData.description);
    formData.append("duration", lessonData.duration);

    if (isEditingLesson) {
      formData.append("lesson_id", editingLessonId);
    }

    if (lessonData.media_path instanceof File) {
      formData.append("media_path", lessonData.media_path);
    } else if (typeof lessonData.media_path === "string" && lessonData.media_path !== "") {
      formData.append("media_path", lessonData.media_path);
    }

    if (lessonData.lesson_thumbnail) {
      formData.append("lesson_thumbnail", lessonData.lesson_thumbnail);
    }

    try {
      const apiUrl = isEditingLesson
        ? SummaryApi.updateLesson.url
        : SummaryApi.createCourseLesson.url;

      const res = await fetch(apiUrl, {
        method: "POST",
        body: formData,
      });

      const data = await res.json();

      if (res.ok && data.status) {
        toast.success(isEditingLesson ? "Lesson updated!" : "Lesson added!");

        // Refetch lessons to get updated list
        const updatedLessons = await fetchLessonsForTopic(showLessonModal);
        const totalDuration = calculateTotalDuration(updatedLessons);

        // Update topic with new lessons and total duration
        setTopics((prevTopics) =>
          prevTopics.map((topic) =>
            topic.id === showLessonModal
              ? { ...topic, lessons: updatedLessons, totalDuration, duration: totalDuration }
              : topic
          )
        );

        // Reset form
        setLessonData({
          name: "",
          video: "",
          description: "",
          duration: "",
          media_path: "",
          lesson_thumbnail: null,
        });
        setShowLessonModal(null);
        setIsEditingLesson(false);
        setEditingLessonId(null);
      } else {
        toast.error("Failed to save lesson.");
      }
    } catch (error) {
      console.error("Error saving lesson:", error);
      toast.error("An error occurred while saving the lesson.");
    } finally {
      setIsSubmittingLesson(false);
    }
  };

  return (
    <div className={`transition-colors duration-300 ${isDarkMode ? 'bg-gray-900 text-white' : 'bg-white text-gray-900'}`}>
      <ToastContainer />

      <h2 className={`text-xl font-semibold mb-4 transition-colors duration-300 ${
        isDarkMode ? 'text-white' : 'text-gray-800'
      }`}>Curriculum</h2>

      {/* Add Topic Button */}
      <div className="flex justify-end mb-4">
        <button
          onClick={() => setShowTopicModal(true)}
  className={`px-4 py-3 text-white rounded flex items-center gap-2 disabled:opacity-70 transition-colors duration-300 ${
                  isDarkMode
                     ? 'bg-[#1C3664] hover:bg-blue-700 text-white'
              : 'bg-[#1C3664] text-white hover:bg-blue-800'
                }`}
        >
          + Add New Topic
        </button>
      </div>

      <div>
        {topics.map((topic) => (
          <div key={topic.id} className={`mb-4 border rounded transition-colors duration-300 ${
            isDarkMode ? 'border-gray-700' : 'border-gray-200'
          }`}>
            <div
              className={`flex justify-between items-center px-4 py-3 cursor-pointer transition-colors duration-300 ${
                isDarkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-gray-100 hover:bg-gray-200'
              }`}
              onClick={() => toggleTopic(topic.id)}
            >
              <h3 className={`font-semibold transition-colors duration-300 ${
                isDarkMode ? 'text-white' : 'text-gray-800'
              }`}>{topic.topic_name}</h3>
              <div className="flex items-center gap-4">
                <FiEdit
                  className={`cursor-pointer transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-400 hover:text-blue-400' : 'text-gray-600 hover:text-blue-600'
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowTopicModal(true);
                    setIsEditingTopic(true);
                    setEditingTopicId(topic.id);
                    setNewTopicData({
                      topic_name: topic.topic_name,
                      day_type: topic.day_type,
                      is_active: topic.is_active === 1,
                    });
                  }}
                />
                {topic.isOpen ? (
                  <FiChevronUp className={`text-xl transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-400' : 'text-gray-600'
                  }`} />
                ) : (
                  <FiChevronDown className={`text-xl transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-400' : 'text-gray-600'
                  }`} />
                )}
              </div>
            </div>

            {topic.isOpen && (
              <div className={`p-4 space-y-2 transition-colors duration-300 ${
                isDarkMode ? 'bg-gray-800' : 'bg-white'
              }`}>
                {loadingLessonsFor === topic.id ? (
                  <div className="flex justify-center py-4">
                    <svg
                      className={`animate-spin h-5 w-5 transition-colors duration-300 ${
                        isDarkMode ? 'text-purple-400' : 'text-purple-600'
                      }`}
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                  </div>
                ) : topic.lessons.length > 0 ? (
                  topic.lessons.map((lesson) => (
                    <div
                      key={lesson.lesson_id}
                      className={`flex justify-between items-center p-2 rounded transition-colors duration-300 ${
                        isDarkMode ? 'bg-green-900' : 'bg-green-50'
                      }`}
                    >
                      <div className={`flex items-center gap-2 transition-colors duration-300 ${
                        isDarkMode ? 'text-green-300' : 'text-green-600'
                      }`}>
                        <div className={`h-3 w-3 rounded-full transition-colors duration-300 ${
                          isDarkMode ? 'bg-green-400' : 'bg-green-500'
                        }`} />
                        {lesson.lesson_name} ({lesson.duration} min)
                      </div>
                      <div className="flex gap-2">
                        <FiEdit
                          className={`cursor-pointer transition-colors duration-300 ${
                            isDarkMode ? 'text-gray-400 hover:text-blue-400' : 'text-gray-500 hover:text-blue-600'
                          }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowLessonModal(topic.id);
                            setIsEditingLesson(true);
                            setEditingLessonId(lesson.lesson_id);
                            setLessonData({
                              name: lesson.lesson_name,
                              video: "",
                              description: lesson.description,
                              duration: lesson.duration,
                              media_path: lesson.media_path || "",
                              lesson_thumbnail: lesson.lesson_thumbnail || null,
                            });
                          }}
                        />
                      </div>
                    </div>
                  ))
                ) : (
                  <p className={`text-sm transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                    No lessons found for this topic.
                  </p>
                )}
                <button
                  onClick={() => setShowLessonModal(topic.id)}
                  className={`mt-2 font-medium hover:underline transition-colors duration-300 ${
                    isDarkMode ? 'text-[#1C3664] hover:text-blue-700 '
              : 'text-[#1C3664]  hover:text-blue-800'
                  }`}
                >
                  + Add Lesson
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Add/Edit Topic Modal */}
      {showTopicModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className={`p-6 rounded shadow-lg w-full max-w-md transition-colors duration-300 ${
            isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white'
          }`}>
            <h3 className={`text-lg font-semibold mb-4 transition-colors duration-300 ${
              isDarkMode ? 'text-white' : 'text-gray-800'
            }`}>
              {isEditingTopic ? "Edit Topic" : "Add New Topic"}
            </h3>

            <div className="mb-4">
              <label className={`block text-sm font-medium mb-1 transition-colors duration-300 ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>Course:</label>
              <input
                type="text"
                className={`w-full px-4 py-3 border rounded transition-colors duration-300 ${
                  isDarkMode 
                    ? 'bg-gray-700 border-gray-600 text-gray-300 cursor-not-allowed' 
                    : 'bg-gray-100 border-gray-300 cursor-not-allowed'
                }`}
                value={courseTitle || "Course Not Selected"}
                readOnly
              />
            </div>

            <label className={`block text-sm font-medium mb-1 transition-colors duration-300 ${
              isDarkMode ? 'text-gray-300' : 'text-gray-700'
            }`}>
              Topic Name:
            </label>
            <input
              type="text"
              placeholder="Topic Name *"
              className={`w-full px-4 py-3 border rounded mb-4 transition-colors duration-300 ${
                isDarkMode 
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:border-blue-500' 
                  : 'border-gray-300 focus:border-purple-500 placeholder-gray-500'
              }`}
              value={newTopicData.topic_name}
              onChange={(e) =>
                setNewTopicData({
                  ...newTopicData,
                  topic_name: e.target.value,
                })
              }
            />

            {/* Duration shown only in Edit mode and disabled */}
            {isEditingTopic && (
              <>
                <label className={`block text-sm font-medium mb-1 transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Total Duration (Auto-calculated):
                </label>
                <div className="flex items-center mb-4">
                  <input
                    type="text"
                    className={`w-full px-4 py-3 border rounded transition-colors duration-300 ${
                      isDarkMode 
                        ? 'bg-gray-700 border-gray-600 text-gray-300 cursor-not-allowed' 
                        : 'bg-gray-100 border-gray-300 cursor-not-allowed'
                    }`}
                    value={`${topics.find(t => t.id === editingTopicId)?.totalDuration || 0} min`}
                    readOnly
                    disabled
                  />
                </div>
              </>
            )}

            <select
              className={`w-full px-4 py-3 border rounded mb-4 transition-colors duration-300 ${
                isDarkMode 
                  ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500' 
                  : 'border-gray-300 focus:border-purple-500'
              }`}
              value={newTopicData.day_type}
              onChange={(e) =>
                setNewTopicData({
                  ...newTopicData,
                  day_type: e.target.value,
                })
              }
            >
              <option value="">Select Day Type</option>
              <option value="weekday">Weekdays</option>
              <option value="weekend">Weekend</option>
            </select>

            <div className="flex items-center space-x-3 mb-6">
              <label
                htmlFor="is_active_toggle_topic"
                className={`text-sm transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}
              >
                Is Active:
              </label>
              <input
                type="checkbox"
                id="is_active_toggle_topic"
                checked={newTopicData.is_active}
                onChange={(e) =>
                  setNewTopicData({
                    ...newTopicData,
                    is_active: e.target.checked,
                  })
                }
                className={`h-4 w-4 border-gray-300 rounded focus:ring-purple-500 transition-colors duration-300 ${
                  isDarkMode ? 'text-purple-500 bg-gray-700' : 'text-purple-600'
                }`}
              />
            </div>

            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowTopicModal(false);
                  setIsEditingTopic(false);
                  setEditingTopicId(null);
                  setNewTopicData({ topic_name: "", day_type: "", is_active: true });
                }}
                className={`px-4 py-3 rounded transition-colors duration-300 ${
                  isDarkMode 
                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                    : 'bg-gray-300 text-gray-700 hover:bg-gray-400'
                }`}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveTopic}
      className={`px-4 py-3 text-white rounded flex items-center gap-2 disabled:opacity-70 transition-colors duration-300 ${
                  isDarkMode
                     ? 'bg-[#1C3664] hover:bg-blue-700 text-white'
              : 'bg-[#1C3664] text-white hover:bg-blue-800'
                }`}
              >
                {isEditingTopic ? "Update Topic" : "Add Topic"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Lesson Modal */}
      {showLessonModal !== null && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className={`p-6 rounded shadow-lg w-full max-w-sm transition-colors duration-300 ${
            isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white'
          }`}>
            <div className="flex justify-between items-center mb-4">
              <h3 className={`text-lg font-semibold transition-colors duration-300 ${
                isDarkMode ? 'text-white' : 'text-gray-800'
              }`}>
                {isEditingLesson ? "Edit Lesson" : "New Lesson"}
              </h3>
              <button
                onClick={() => {
                  setShowLessonModal(null);
                  setIsEditingLesson(false);
                  setEditingLessonId(null);
                  setLessonData({
                    name: "",
                    video: "",
                    description: "",
                    duration: "",
                    media_path: "",
                    lesson_thumbnail: null,
                  });
                }}
                className={`text-xl transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                ×
              </button>
            </div>

            <label className={`block text-sm font-medium mb-1 transition-colors duration-300 ${
              isDarkMode ? 'text-gray-300' : 'text-gray-700'
            }`}>
              Lesson Name:
            </label>
            <input
              type="text"
              className={`w-full px-4 py-3 border rounded mb-3 transition-colors duration-300 ${
                isDarkMode 
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:border-blue-500' 
                  : 'border-gray-300 focus:border-purple-500 placeholder-gray-500'
              }`}
              placeholder="Lesson Name *"
              value={lessonData.name}
              onChange={(e) =>
                setLessonData({ ...lessonData, name: e.target.value })
              }
            />

            <label className={`block text-sm font-medium mb-1 transition-colors duration-300 ${
              isDarkMode ? 'text-gray-300' : 'text-gray-700'
            }`}>Duration:</label>
            <div className="flex items-center mb-4">
              <input
                type="text"
                className={`w-full px-4 py-3 border rounded transition-colors duration-300 ${
                  isDarkMode 
                    ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:border-blue-500' 
                    : 'border-gray-300 focus:border-purple-500 placeholder-gray-500'
                }`}
                placeholder="Duration (e.g. 30)"
                value={lessonData.duration}
                onChange={(e) =>
                  setLessonData({
                    ...lessonData,
                    duration: e.target.value,
                  })
                }
              />
              <span className={`ml-2 transition-colors duration-300 ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>min</span>
            </div>

            <label className={`block text-sm font-medium mb-1 transition-colors duration-300 ${
              isDarkMode ? 'text-gray-300' : 'text-gray-700'
            }`}>
              Description:
            </label>
            <textarea
              className={`w-full px-4 py-3 border rounded mb-3 transition-colors duration-300 ${
                isDarkMode 
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:border-blue-500' 
                  : 'border-gray-300 focus:border-purple-500 placeholder-gray-500'
              }`}
              placeholder="Lesson Description"
              value={lessonData.description}
              onChange={(e) =>
                setLessonData({
                  ...lessonData,
                  description: e.target.value,
                })
              }
            />

            <label className={`block mb-1 font-medium transition-colors duration-300 ${
              isDarkMode ? 'text-gray-300' : 'text-gray-700'
            }`}>
              Upload Video File (or paste URL)
            </label>
            <input
              type="file"
              accept="video/*"
              className={`w-full px-4 py-3 border rounded mb-3 transition-colors duration-300 ${
                isDarkMode 
                  ? 'bg-gray-700 border-gray-600 text-gray-300' 
                  : 'border-gray-300'
              }`}
              onChange={(e) =>
                setLessonData({
                  ...lessonData,
                  media_path: e.target.files[0] || "",
                })
              }
            />
            <input
              type="text"
              placeholder="Or paste YouTube/Vimeo/External URL"
              className={`w-full px-4 py-3 border rounded mb-3 transition-colors duration-300 ${
                isDarkMode 
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                  : 'border-gray-300 placeholder-gray-500'
              }`}
              value={typeof lessonData.media_path === "string" ? lessonData.media_path : ""}
              onChange={(e) =>
                setLessonData({ ...lessonData, media_path: e.target.value })
              }
              disabled={lessonData.media_path instanceof File}
            />

            {/* THUMBNAIL SECTION */}
            <label className={`block mb-1 font-medium transition-colors duration-300 ${
              isDarkMode ? 'text-gray-300' : 'text-gray-700'
            }`}>
              Upload Image File (Thumbnail)
            </label>

            {lessonData.lesson_thumbnail && (
              <div className="mb-4">
                <p className={`text-sm font-medium mb-2 transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>Current Thumbnail:</p>
                <img
                  src={
                    lessonData.lesson_thumbnail instanceof File
                      ? URL.createObjectURL(lessonData.lesson_thumbnail)
                      : formatImageUrl(lessonData.lesson_thumbnail)
                  }
                  alt="Thumbnail Preview"
                  className="w-full max-w-md h-28 object-cover rounded-lg border shadow-sm"
                  onError={(e) => {
                    e.target.src = "https://via.placeholder.com/500x112?text=Image+Not+Found";
                  }}
                />
              </div>
            )}
            <input
              type="file"
              accept="image/*"
              onChange={(e) =>
                setLessonData({
                  ...lessonData,
                  lesson_thumbnail: e.target.files[0] || null,
                })
              }
              className={`w-full px-4 py-3 border rounded mb-3 transition-colors duration-300 ${
                isDarkMode 
                  ? 'bg-gray-700 border-gray-600 text-gray-300' 
                  : 'border-gray-300'
              }`}
            />

            <div className="flex justify-end space-x-2">
              <button
                onClick={() => {
                  setShowLessonModal(null);
                  setIsEditingLesson(false);
                  setEditingLessonId(null);
                  setLessonData({
                    name: "",
                    video: "",
                    description: "",
                    duration: "",
                    media_path: "",
                    lesson_thumbnail: null,
                  });
                }}
                className={`px-4 py-3 rounded transition-colors duration-300 ${
                  isDarkMode 
                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                    : 'bg-gray-300 text-gray-700 hover:bg-gray-400'
                }`}
              >
                Cancel
              </button>

              <button
                onClick={handleSaveLesson}
                disabled={isSubmittingLesson}
                className={`px-4 py-3 text-white rounded flex items-center gap-2 disabled:opacity-70 transition-colors duration-300 ${
                  isDarkMode 
               ? 'bg-[#1C3664] hover:bg-blue-700 text-white'
              : 'bg-[#1C3664] text-white hover:bg-blue-800'
                }`}
              >
                {isSubmittingLesson ? (
                  <>
                    <svg
                      className="animate-spin h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    {isEditingLesson ? "Updating..." : "Adding..."}
                  </>
                ) : (
                  <>{isEditingLesson ? "Update Lesson" : "Add Lesson"}</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CurriculumStep;